<template>
  <div class="mode-selector">
    <div class="mode-header">
      <h2>{{ t('games.poker.title') }}</h2>
      <p class="mode-subtitle">{{ t('games.poker.description') }}</p>
    </div>

    <div class="modes-container">
      <div
        v-for="(mode, index) in modeOptions"
        :key="mode.code"
        class="mode-card"
        :class="`mode-${mode.code}`"
      >
        <div class="mode-icon">{{ modeIcons[index] }}</div>
        <h3 class="mode-name">{{ mode.name }}</h3>
        <p class="mode-description">{{ mode.description }}</p>
        <div class="mode-details">
          <span v-for="detail in mode.details" :key="detail" class="detail-tag">
            {{ detail }}
          </span>
        </div>
        <button class="mode-btn" @click="selectMode(mode.code)">
          {{ t('buttons.quickStart') }}
        </button>
      </div>
    </div>

    <button class="btn-back" @click="goHome">
      {{ t('buttons.backHome') }}
    </button>
  </div>
</template>

<script setup>
import { useI18n } from '@/utils/i18n'
import { computed } from 'vue'

const { t } = useI18n()

const modeOptions = computed(() => {
  const details = t('modes.classicDetails')
  return [
    {
      code: 'classic',
      name: t('modes.classic'),
      description: t('modes.classicDesc'),
      details: Array.isArray(details) ? details : ['Turn-based', '3-10 rounds', 'Score based']
    },
    {
      code: 'lightning',
      name: t('modes.lightning'),
      description: t('modes.lightningDesc'),
      details: Array.isArray(t('modes.lightningDetails')) ? t('modes.lightningDetails') : ['Real-time', '60s timer', 'Speed matters']
    },
    {
      code: 'coop',
      name: t('modes.coop'),
      description: t('modes.coopDesc'),
      details: Array.isArray(t('modes.coopDetails')) ? t('modes.coopDetails') : ['Team-based', 'Cooperation', 'Pool sharing']
    }
  ]
})

const modeIcons = ['♟️', '⚡', '🤝']

const selectMode = (modeCode) => {
  window.location.hash = `#/games/poker/${modeCode}`
}

const goHome = () => {
  window.location.hash = '#/'
}
</script>

<style scoped>
.mode-selector {
  min-height: calc(100vh - 64px);
  padding: 40px 24px;
  background: linear-gradient(135deg, var(--color-bg-light) 0%, var(--color-bg-card) 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
}

.mode-header {
  text-align: center;
  max-width: 600px;
}

.mode-header h2 {
  margin: 0 0 12px 0;
  font-size: 36px;
  color: var(--color-text-primary);
  font-weight: 700;
}

.mode-subtitle {
  margin: 0;
  font-size: 16px;
  color: var(--color-text-secondary);
  line-height: 1.6;
}

.modes-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  width: 100%;
  max-width: 1100px;
}

.mode-card {
  background: var(--color-bg-card);
  border: 2px solid var(--color-border);
  border-radius: 16px;
  padding: 32px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: var(--shadow-sm);
}

.mode-card:hover {
  transform: translateY(-8px);
  border-color: var(--color-primary-orange);
  box-shadow: var(--shadow-lg);
}

.mode-card.mode-classic {
  --accent-color: #FF6B35;
}

.mode-card.mode-lightning {
  --accent-color: #FFA500;
}

.mode-card.mode-coop {
  --accent-color: #FF9A76;
}

.mode-card:hover {
  border-color: var(--accent-color);
  background: linear-gradient(135deg, rgba(255, 107, 53, 0.02) 0%, rgba(255, 107, 53, 0.05) 100%);
}

.mode-icon {
  font-size: 48px;
  margin-bottom: 16px;
  display: block;
}

.mode-name {
  margin: 0 0 12px 0;
  font-size: 22px;
  font-weight: 700;
  color: var(--color-text-primary);
}

.mode-description {
  margin: 0 0 20px 0;
  font-size: 14px;
  color: var(--color-text-secondary);
  line-height: 1.6;
  min-height: 42px;
}

.mode-details {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  margin-bottom: 20px;
  width: 100%;
}

.detail-tag {
  display: inline-block;
  padding: 6px 12px;
  background: rgba(255, 107, 53, 0.1);
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  color: var(--color-primary-orange);
  border: 1px solid rgba(255, 107, 53, 0.3);
}

.mode-btn {
  width: 100%;
  padding: 12px 24px;
  background: linear-gradient(135deg, #FF6B35 0%, #FFA500 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: auto;
}

.mode-btn:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(255, 107, 53, 0.3);
}

.btn-back {
  padding: 12px 32px;
  background: var(--color-bg-card);
  border: 2px solid var(--color-border);
  border-radius: 8px;
  color: var(--color-text-primary);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
}

.btn-back:hover {
  border-color: var(--color-primary-orange);
  color: var(--color-primary-orange);
  background: rgba(255, 107, 53, 0.05);
}

/* 响应式 */
@media (max-width: 768px) {
  .mode-selector {
    padding: 24px 16px;
    gap: 32px;
  }

  .mode-header h2 {
    font-size: 28px;
  }

  .modes-container {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .mode-card {
    padding: 24px 16px;
  }
}
</style>
